import { cn } from "./cn";
import { useTenantTheme } from "@/lib/tenant-theme";
import { useOrg } from "@/lib/org-context";

/**
 * Brand mark: the platform's logo, with the product's name where there is room.
 *
 * Two variants:
 *   - `"mark"` (default): the hexagon logomark only (forge crucible + anvil +
 *     sparks fused with a digital-twin node graph). Fits a small ~32px square —
 *     used in the app top bar, sidebar drawer, and footers.
 *   - `"full"`: the mark with the product's name beside it (PRODUCT_NAME, "Foundry Twin", 2026-10-06; the old
 *     lockup image drew the internal name into its wordmark). Used on marketing and auth surfaces where there is
 *     room: the landing top bar, the login hero, the insights chrome header. Callers using `"full"` must NOT
 *     render a separate product-name label beside it.
 *
 * Tenant override: when an org has uploaded a custom logo via the Branding
 * admin panel (B0.39 / B0.40), `tenant.logoUrl` always wins — the platform
 * logo only shows when the org hasn't supplied their own.
 *
 * Dark mode: the platform logo is a dark (near-black) trace, so on a dark
 * surface it would vanish. The `dark:invert` utility flips it to light on dark
 * surfaces. This relies on the `@custom-variant dark` wired in index.css, which
 * maps Tailwind's `dark:` to this app's `data-theme`-driven dark mode. The
 * tenant's own logo is left untouched (we don't know its colours).
 */
export function BrandMark({
  className,
  variant = "mark",
}: {
  className?: string;
  variant?: "mark" | "full";
}) {
  const tenant = useTenantTheme();

  // Tenant org-logo override — always wins, sized to fit the same box. Kept
  // as a contained square so a tall/wide org logo never blows out layout.
  if (tenant.logoUrl) {
    return (
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface-elevated shadow-sm",
          className,
        )}
        aria-label={tenant.productName}
      >
        <img
          src={tenant.logoUrl}
          alt={`${tenant.productName} logo`}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }

  // Full lockup — mark + wordmark in one image. Sized by height so the aspect
  // ratio is preserved; callers control the height via `className` (e.g.
  // `h-9`). No background box: the lockup sits directly on the surface.
  if (variant === "full") {
    return (
      <span
        role="img"
        aria-label={tenant.productName}
        className={cn("inline-flex h-9 w-auto shrink-0 items-center gap-2", className)}
      >
        <img src="/logo-mark.png" alt="" className="h-full w-auto object-contain dark:invert" />
        <span className="whitespace-nowrap text-lg font-semibold tracking-tight text-on-surface">
          {tenant.productName}
        </span>
      </span>
    );
  }

  // Mark only — the hexagon logomark, sized to the standard 32px square box.
  return (
    <img
      src="/logo-mark.png"
      alt={tenant.productName}
      className={cn(
        "size-8 shrink-0 object-contain dark:invert",
        className,
      )}
    />
  );
}

/**
 * Bundled vendor wordmarks for the co-brand (until per-vendor `org_branding`
 * logo uploads are resolvable by `viaVendorOrgId`). Add an entry per vendor that
 * ships an asset in `apps/web/public/`; unmatched vendors render their name as
 * text. Matched on the vendor's display name (from `/auth/me`).
 */
const VENDOR_LOGOS: { test: RegExp; src: string }[] = [
  { test: /versatile|vepl/i, src: "/versatile-logo.png" },
];

/**
 * Co-brand mark — the Sahas platform mark beside the vendor wordmark, shown
 * when the active org was onboarded *via* a vendor (e.g. Versatile):
 * `activeOrg.viaVendorOrgId` is set (sourced from /v1/platform/auth/me →
 * org_branding.vendor_org_id). The co-brand is driven by the org-overlay flag,
 * NOT the applied theme or the build, so a Versatile-onboarded foundry keeps
 * the dual mark even after it "graduates" to the full twin. When there's no
 * vendor origin it delegates to <BrandMark>, so it's a drop-in replacement in
 * the app chrome. Must render under OrgProvider (the /app shell). See
 * docs/versatile/specs/tenancy-vendor-portal.md §4.4.
 */
export function CoBrandMark({
  className,
  variant = "mark",
}: {
  className?: string;
  variant?: "mark" | "full";
}) {
  const { activeOrg } = useOrg();

  // No vendor origin → ordinary single platform mark.
  if (!activeOrg?.viaVendorOrgId) {
    return <BrandMark className={className} variant={variant} />;
  }

  // Vendor-onboarded org → [Sahas mark] · [vendor wordmark]. The wordmark is the
  // vendor's OWN name from /auth/me (orgs.name of viaVendorOrgId), so a 2nd
  // vendor shows its own name with no code change — no literal. Falls back to a
  // neutral label only if the name is somehow absent (older API build).
  const vendorName = activeOrg.vendorName?.trim() || "Vendor";
  // Bundled vendor wordmark lookup. Interim until per-vendor branding assets are
  // resolvable by viaVendorOrgId (an org_branding logo upload) — keyed on the
  // vendor's display name so a future vendor with no bundled asset cleanly falls
  // back to its text wordmark. The asset preserves the vendor's own brand colour
  // (no `dark:invert` — we don't own its palette).
  const vendorLogoSrc = VENDOR_LOGOS.find((v) => v.test.test(vendorName))?.src ?? null;
  return (
    <span className={cn("flex items-center gap-2", className)}>
      <BrandMark variant="mark" />
      <span aria-hidden className="text-base text-on-surface-muted">
        ·
      </span>
      {vendorLogoSrc ? (
        <img
          src={vendorLogoSrc}
          alt={vendorName}
          className="h-5 w-auto shrink-0"
        />
      ) : (
        <span className="whitespace-nowrap text-sm font-semibold tracking-tight text-on-surface">
          {vendorName}
        </span>
      )}
    </span>
  );
}
